import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { logEvent } from '@amplitude/analytics-browser';
import Box from '@mui/material/Box';
import {
  useAppSelector,
  useAppDispatch,
  createCompany,
  updateCompany,
  completeOnboarding,
  loadCompanyUser,
} from '../../store';
import { useDocuments } from '../../hooks/documents';
import { useLazyTranslation } from '../../hooks/i18n';
import Skeleton from '../../componentsv2/Skeleton';
import { useSnackbar } from '../../context';
import { DASHBOARD } from '../../constants/auth';
import { backendCountryValues } from '../../constants/data';
import { activationEvents } from '../../constants/events';
import { identifyCompany } from '../../utils/tracking';
import { CountryKeys } from '../../types';
import { CompanyOnboardingStep, CompanyUser, SatCredentialStatus } from '../../types/companyUser';
import ActivationLayout from './ActivationLayout';
import SelectCountry from './steps/SelectCountry';
import CompanyInformation, { FormValues as CompanyInfoFormValues } from './steps/CompanyInformation';
import Documents, { DocumentSlotFiles } from './steps/Documents';
import { DocumentSlotKey, getRequiredDocumentSlots } from '../../constants/data';
import Ownership from './steps/Ownership';
import SyntangeConnection from './steps/SyntangeConnection';
import ConnectSAT from './steps/ConnectSAT';

const COMPANY_ALREADY_COMPLETED_ERROR_CODE = 'company_already_completed_error';

const DOCUMENT_KEY_NAME_MAP: Record<DocumentSlotKey, string> = {
  taxIdentificationLetter: 'Tax Identification Letter',
  operatingAgreement: 'Operating Agreement (Applicable if LLC)',
  certificateOfFormation: 'Certificate of Formation (Articles of Organization or Incorporation)',
  ownerRegister: 'BOI (Beneficial Ownership Information Report)',
  actaConstitutiva: 'Certificate of Formation (Articles of Organization or Incorporation)',
  protocolizacion: 'Certificate of Formation (Articles of Organization or Incorporation)',
};

const onboardingStepToNumber: Record<string, number> = {
  [CompanyOnboardingStep.Initial]: 1,
  [CompanyOnboardingStep.CountrySelection]: 1,
  [CompanyOnboardingStep.CompanyInformation]: 2,
  [CompanyOnboardingStep.DocumentsUpload]: 3,
  [CompanyOnboardingStep.OwnershipStructure]: 4,
};

const stepNumberToKey: Record<number, string> = {
  1: CompanyOnboardingStep.CountrySelection,
  2: CompanyOnboardingStep.CompanyInformation,
  3: CompanyOnboardingStep.DocumentsUpload,
  4: CompanyOnboardingStep.OwnershipStructure,
};

const Activation = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const location = useLocation();
  const { t, isLoading: isLoadingTranslations } = useLazyTranslation(['activation']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { draftCompany } = useAppSelector((state) => state.companyUser.companyUser) || ({} as CompanyUser);
  const companies = useAppSelector((state) => state.companyUser.companyUser?.companies) || [];
  const status = useAppSelector((state) => state.companyUser.status);
  const { showSnackbar } = useSnackbar();

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [showSyntageModal, setShowSyntageModal] = useState(false);
  const [preloadedRfc, setPreloadedRfc] = useState<string | undefined>(undefined);
  const hasStartedFlow = useRef(false);
  const hasCompletedFlow = useRef(false);
  const lastTrackedStep = useRef<number | null>(null);
  const currentStepRef = useRef<number>(1);
  const skipStepSyncRef = useRef(false);
  const isInitializing = isLoadingTranslations || !draftCompany;
  const shouldCreateDraftFromNavigation = Boolean(
    (location.state as { createNewDraft?: boolean } | null)?.createNewDraft
  );
  const satRfcFromNavigation = (location.state as { satRfc?: string } | null)?.satRfc;

  const hubspotCompanyId = params.get('hubspot_company_id');
  const referralId = params.get('referral_id');
  const { loadCompanyCategories, groupedCategoriesByDocType, uploadAssets, deleteAsset } = useDocuments(
    draftCompany?.id,
    'en',
    false
  );

  const allDocumentCategories = Object.values(groupedCategoriesByDocType).flatMap(({ groupedCategories }) =>
    Object.values(groupedCategories).flat()
  );

  const getCategoryBySlot = (slotKey: DocumentSlotKey) =>
    allDocumentCategories.find((cat) => cat.keyName === DOCUMENT_KEY_NAME_MAP[slotKey]);

  const companyCountry = (draftCompany?.country as CountryKeys) || 'us';
  const companyEntityType = draftCompany?.businessStructure || '';
  const requiredSlots = getRequiredDocumentSlots(companyCountry, companyEntityType);

  const isAnyDocumentCategoryLoading = requiredSlots.some((slot) => getCategoryBySlot(slot)?.isLoading);

  const existingDocumentAssets = Object.fromEntries(
    requiredSlots
      .map((slot) => [slot, getCategoryBySlot(slot)?.assets ?? []])
      .filter(([, assets]) => (assets as unknown[]).length > 0)
  ) as Partial<Record<DocumentSlotKey, typeof allDocumentCategories[0]['assets']>>;

  // Guard: if the user deleted all required documents but onboardingStep was already advanced,
  // roll back to the documents step to prevent landing on step 4 with no documents.
  const allRequiredDocumentsUploaded = requiredSlots.every(
    (slot) => (getCategoryBySlot(slot)?.assets ?? []).length > 0
  );

  // Decide once on mount whether a draft needs to be created.
  // Using [] intentionally — this is a mount-time decision, not a reactive one.
  // Re-evaluating on state changes causes spurious createCompany calls after completeOnboarding.
  //
  // We also immediately clear the `createNewDraft` navigation state (replace history entry)
  // so that if this component unmounts and remounts mid-flow (e.g. due to the global
  // isPending backdrop in App.tsx briefly toggling the Routes tree), the flag is gone and
  // the draft is NOT created a second time.
  useEffect(() => {
    const shouldCreateDraft = companies.length === 0 || shouldCreateDraftFromNavigation;
    if (draftCompany === null && shouldCreateDraft) {
      dispatch(createCompany({ hubspotCompanyId, referralId }));
    }
    if (shouldCreateDraftFromNavigation) {
      navigate(location.pathname, { state: {}, replace: true });
    }
    if (satRfcFromNavigation) {
      skipStepSyncRef.current = true;
      setPreloadedRfc(satRfcFromNavigation);
      setCurrentStep(2);
      setShowSyntageModal(true);
      navigate(location.pathname, { state: {}, replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (draftCompany?.id) {
      loadCompanyCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftCompany?.id]);

  // Initialize step from draftCompany.onboardingStep when it becomes available
  useEffect(() => {
    if (draftCompany?.onboardingStep && !skipStepSyncRef.current) {
      setCurrentStep(onboardingStepToNumber[draftCompany.onboardingStep] ?? 1);
    }
  }, [draftCompany]);

  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  const getSharedTrackingProps = (stepNumber: number) => ({
    StepNumber: stepNumber,
    StepKey: stepNumberToKey[stepNumber],
    CompanyId: draftCompany?.id,
    CompanyName: draftCompany?.name || undefined,
    OnboardingStepBefore: draftCompany?.onboardingStep || CompanyOnboardingStep.Initial,
    Path: location.pathname,
  });

  useEffect(() => {
    if (!isInitializing && draftCompany?.id && !hasStartedFlow.current) {
      const trackingProps = getSharedTrackingProps(currentStepRef.current);
      identifyCompany({ companyName: draftCompany.name, country: draftCompany.country });
      logEvent(activationEvents.VIEW_PAGE, trackingProps);
      logEvent(activationEvents.START_FLOW, trackingProps);
      hasStartedFlow.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitializing, draftCompany?.id]);

  useEffect(() => {
    if (isInitializing || !draftCompany?.id) {
      return;
    }
    if (lastTrackedStep.current === currentStep) {
      return;
    }
    lastTrackedStep.current = currentStep;
    logEvent(activationEvents.VIEW_STEP, getSharedTrackingProps(currentStep));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, isInitializing, draftCompany?.id]);

  useEffect(() => {
    return () => {
      if (hasStartedFlow.current && !hasCompletedFlow.current) {
        logEvent(activationEvents.ABANDON_FLOW, {
          ...getSharedTrackingProps(currentStepRef.current),
          LastSeenStep: currentStepRef.current,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the user deleted their documents but the onboardingStep update failed,
  // the backend may still say 'ownership_structure' while there are no uploaded files.
  // Correct the local step back to documents_upload to avoid landing on step 4 with no documents.
  useEffect(() => {
    if (
      allDocumentCategories.length > 0 &&
      !allRequiredDocumentsUploaded &&
      draftCompany?.onboardingStep === CompanyOnboardingStep.OwnershipStructure
    ) {
      setCurrentStep(3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRequiredDocumentsUploaded, draftCompany?.onboardingStep]);

  const handleBack = () => {
    if (currentStep > 1) {
      logEvent(activationEvents.CLICK_BACK, {
        ...getSharedTrackingProps(currentStep),
        ToStepNumber: currentStep - 1,
        ToStepKey: stepNumberToKey[currentStep - 1],
      });
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCountrySubmit = async (country: CountryKeys) => {
    if (!draftCompany) return;
    logEvent(activationEvents.CLICK_NEXT, getSharedTrackingProps(1));
    logEvent(activationEvents.STEP_SUBMIT_ATTEMPT, {
      ...getSharedTrackingProps(1),
      Country: country,
    });
    try {
      await dispatch(
        updateCompany({
          companyId: draftCompany.id,
          payload: {
            country: backendCountryValues[country],
            onboardingStep: CompanyOnboardingStep.CompanyInformation,
          },
        })
      ).unwrap();
      identifyCompany({ country: backendCountryValues[country] });
      logEvent(activationEvents.STEP_SUBMIT_SUCCESS, {
        ...getSharedTrackingProps(1),
        Country: country,
        OnboardingStepAfter: 'company_information',
      });
      setCurrentStep(2);
    } catch (error) {
      logEvent(activationEvents.STEP_SUBMIT_ERROR, {
        ...getSharedTrackingProps(1),
        Country: country,
        ErrorType: 'api',
      });
      console.error('Error saving country:', error);
      showSnackbar(t('activation:errors.saveFailed'), 'error');
    }
  };

  const handleCompanyInfoSubmit = async (values: CompanyInfoFormValues) => {
    if (!draftCompany) return;
    // draftCompany.name is not updated yet in this closure, so use the name typed in the form
    const companyNameProps = { CompanyName: values.companyName };
    logEvent(activationEvents.CLICK_NEXT, { ...getSharedTrackingProps(2), ...companyNameProps });
    logEvent(activationEvents.STEP_SUBMIT_ATTEMPT, {
      ...getSharedTrackingProps(2),
      ...companyNameProps,
      Country: draftCompany.country,
      EntityType: values.entityType || 'not_provided',
      TaxIdType: values.taxIdType,
    });
    try {
      await dispatch(
        updateCompany({
          companyId: draftCompany.id,
          payload: {
            name: values.companyName,
            businessStructure: values.entityType,
            formationDate: values.formationDate ? dayjs(values.formationDate).format('YYYY-MM-DD') : null,
            taxIdType: values.taxIdType,
            taxId: values.taxId,
            addressStreetOne: values.businessStreetAddress,
            addressCity: values.city,
            addressState: values.state,
            addressZipCode: values.zipCode,
            onboardingStep: CompanyOnboardingStep.DocumentsUpload,
          },
        })
      ).unwrap();
      identifyCompany({ companyName: values.companyName });
      logEvent(activationEvents.STEP_SUBMIT_SUCCESS, {
        ...getSharedTrackingProps(2),
        ...companyNameProps,
        Country: draftCompany.country,
        EntityType: values.entityType || 'not_provided',
        TaxIdType: values.taxIdType,
        OnboardingStepAfter: 'documents_upload',
      });
      setCurrentStep(3);
    } catch (error) {
      logEvent(activationEvents.STEP_SUBMIT_ERROR, {
        ...getSharedTrackingProps(2),
        ...companyNameProps,
        Country: draftCompany.country,
        EntityType: values.entityType || 'not_provided',
        TaxIdType: values.taxIdType,
        ErrorType: 'api',
      });
      console.error('Error saving company info:', error);
      showSnackbar(t('activation:errors.saveFailed'), 'error');
    }
  };

  const handleDocumentsNext = async (files: DocumentSlotFiles) => {
    const totalFileCount = Object.values(files).reduce((sum, f) => sum + (f?.length ?? 0), 0);
    logEvent(activationEvents.CLICK_NEXT, getSharedTrackingProps(3));
    logEvent(activationEvents.STEP_SUBMIT_ATTEMPT, {
      ...getSharedTrackingProps(3),
      FileCount: totalFileCount,
    });
    try {
      const slotsToUpload = Object.entries(files) as [DocumentSlotKey, File[]][];
      if (slotsToUpload.length > 0) {
        logEvent(activationEvents.DOCUMENT_UPLOAD_ATTEMPT, {
          ...getSharedTrackingProps(3),
          FileCount: totalFileCount,
        });
        await Promise.all(
          slotsToUpload.map(([slotKey, slotFiles]) => {
            const category = getCategoryBySlot(slotKey);
            if (!category || !slotFiles?.length) return Promise.resolve();
            return uploadAssets(
              category.id,
              category.documentId,
              slotFiles,
              category.name,
              category.groupName
            );
          })
        );
        logEvent(activationEvents.DOCUMENT_UPLOAD_SUCCESS, {
          ...getSharedTrackingProps(3),
          FileCount: totalFileCount,
        });
      }
      await dispatch(
        updateCompany({
          companyId: draftCompany!.id,
          payload: { onboardingStep: CompanyOnboardingStep.OwnershipStructure },
        })
      ).unwrap();
      logEvent(activationEvents.STEP_SUBMIT_SUCCESS, {
        ...getSharedTrackingProps(3),
        FileCount: totalFileCount,
        OnboardingStepAfter: 'ownership_structure',
      });
      setCurrentStep(4);
    } catch (error) {
      if (totalFileCount > 0) {
        logEvent(activationEvents.DOCUMENT_UPLOAD_ERROR, {
          ...getSharedTrackingProps(3),
          FileCount: totalFileCount,
          ErrorType: 'upload_or_api',
        });
      }
      logEvent(activationEvents.STEP_SUBMIT_ERROR, {
        ...getSharedTrackingProps(3),
        FileCount: totalFileCount,
        ErrorType: 'api',
      });
      console.error('Error uploading documents:', error);
      showSnackbar(t('activation:step3.uploadError'), 'error');
    }
  };

  const handleDeleteAsset = async (slotKey: DocumentSlotKey, assetId: number) => {
    if (!draftCompany) return;
    const category = getCategoryBySlot(slotKey);
    if (!category) return;
    try {
      deleteAsset(category.id, category.documentId, assetId);
      await dispatch(
        updateCompany({
          companyId: draftCompany.id,
          payload: { onboardingStep: CompanyOnboardingStep.DocumentsUpload },
        })
      ).unwrap();
    } catch (error) {
      console.error('Error updating onboarding step:', error);
      showSnackbar(t('activation:step3.deleteError'), 'error');
    }
  };

  const handleSyntageNext = async () => {
    if (preloadedRfc) {
      try {
        await dispatch(completeOnboarding(draftCompany!.id)).unwrap();
        skipStepSyncRef.current = false;
        await dispatch(loadCompanyUser()).unwrap();
        navigate(DASHBOARD);
      } catch (error: any) {
        if (error?.error_code === COMPANY_ALREADY_COMPLETED_ERROR_CODE) {
          try {
            await dispatch(loadCompanyUser()).unwrap();
          } catch (refetchError) {
            console.error('Error refreshing company user after duplicate submit:', refetchError);
          }
          showSnackbar(t('activation:success.alreadyCompleted'), 'success');
          navigate(DASHBOARD);
          return;
        }
        showSnackbar(t('activation:step4.error'), 'error');
      }
    } else {
      skipStepSyncRef.current = false;
      setCurrentStep(3);
    }
  };

  const handleCompleteActivation = async ({
    shareholderCount,
    controllingPersonCount,
  }: {
    shareholderCount: number;
    controllingPersonCount: number;
  }) => {
    logEvent(activationEvents.CLICK_NEXT, getSharedTrackingProps(4));
    logEvent(activationEvents.STEP_SUBMIT_ATTEMPT, {
      ...getSharedTrackingProps(4),
      ShareholderCount: shareholderCount,
      ControllingPersonCount: controllingPersonCount,
    });
    try {
      if (draftCompany?.productStatus?.satCredentialStatus === SatCredentialStatus.Invalid) {
        hasCompletedFlow.current = true;
        setPreloadedRfc(draftCompany?.taxId ?? undefined);
        setCurrentStep(2);
        setShowSyntageModal(true);
        showSnackbar(t('activation:mxSat.invalidCredential'), 'error');
        return;
      }

      await dispatch(completeOnboarding(draftCompany!.id)).unwrap();
      hasCompletedFlow.current = true;
      logEvent(activationEvents.STEP_SUBMIT_SUCCESS, {
        ...getSharedTrackingProps(4),
        ShareholderCount: shareholderCount,
        ControllingPersonCount: controllingPersonCount,
        OnboardingStepAfter: 'completed',
      });
      logEvent(activationEvents.COMPLETE_FLOW, {
        ...getSharedTrackingProps(4),
        ShareholderCount: shareholderCount,
        ControllingPersonCount: controllingPersonCount,
        ToRoute: DASHBOARD,
      });
      await dispatch(loadCompanyUser()).unwrap();
      navigate(DASHBOARD);
    } catch (error: any) {
      if (error?.error_code === COMPANY_ALREADY_COMPLETED_ERROR_CODE) {
        logEvent(activationEvents.STEP_SUBMIT_SUCCESS, {
          ...getSharedTrackingProps(4),
          ShareholderCount: shareholderCount,
          ControllingPersonCount: controllingPersonCount,
          OnboardingStepAfter: 'completed',
        });
        try {
          await dispatch(loadCompanyUser()).unwrap();
        } catch (refetchError) {
          console.error('Error refreshing company user after duplicate submit:', refetchError);
        }
        showSnackbar(t('activation:success.alreadyCompleted'), 'success');
        navigate(DASHBOARD);
        return;
      }
      logEvent(activationEvents.STEP_SUBMIT_ERROR, {
        ...getSharedTrackingProps(4),
        ShareholderCount: shareholderCount,
        ControllingPersonCount: controllingPersonCount,
        ErrorType: 'api',
      });
      console.error('Error completing activation:', error);
      showSnackbar(t('activation:errors.saveFailed'), 'error');
    }
  };

  const companyInfoInitialValues: CompanyInfoFormValues | undefined = draftCompany
    ? {
        companyName: draftCompany.name || '',
        entityType: draftCompany.businessStructure || '',
        formationDate: draftCompany.formationDate ? dayjs(draftCompany.formationDate) : null,
        taxIdType: draftCompany.taxIdType || 'EIN',
        taxId: draftCompany.taxId || '',
        businessStreetAddress: draftCompany.addressStreetOne || '',
        city: draftCompany.addressCity || '',
        state: draftCompany.addressState || '',
        zipCode: draftCompany.addressZipCode || '',
      }
    : undefined;

  const renderStep = () => {
    if (isInitializing) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', flex: 1 }}>
          <Skeleton height={40} width="60%" />
          <Skeleton height={24} width="80%" />
          <Skeleton height={200} />
          <Skeleton height={48} width="30%" sx={{ alignSelf: 'flex-end' }} />
        </Box>
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <SelectCountry
            initialCountry={(draftCompany?.country as CountryKeys) || undefined}
            onSubmit={handleCountrySubmit}
            isLoading={status === 'loading'}
          />
        );
      case 2:
        if (companyCountry === 'mx') {
          return (
            <>
              <ConnectSAT
                onConnect={() => setShowSyntageModal(true)}
                onBack={handleBack}
                isLoading={status === 'loading'}
              />
              {showSyntageModal && (
                <SyntangeConnection
                  onBack={() => {
                    skipStepSyncRef.current = false;
                    setShowSyntageModal(false);
                  }}
                  onNext={handleSyntageNext}
                  isLoading={status === 'loading'}
                  companyId={draftCompany?.id}
                  initialRfc={preloadedRfc}
                />
              )}
            </>
          );
        }
        return (
          <CompanyInformation
            country={draftCompany!.country as CountryKeys}
            initialValues={companyInfoInitialValues}
            onSubmit={handleCompanyInfoSubmit}
            onBack={handleBack}
            isLoading={status === 'loading'}
          />
        );
      case 3:
        return (
          <Documents
            country={companyCountry}
            entityType={companyEntityType}
            onNext={handleDocumentsNext}
            onBack={handleBack}
            isUploading={isAnyDocumentCategoryLoading || status === 'loading'}
            existingAssets={existingDocumentAssets}
            onDeleteAsset={handleDeleteAsset}
          />
        );
      case 4:
        return (
          <Ownership
            companyId={draftCompany!.id}
            companyName={draftCompany!.name}
            onNext={handleCompleteActivation}
            onBack={handleBack}
            isNextLoading={status === 'loading'}
          />
        );
      default:
        return null;
    }
  };

  return <ActivationLayout currentStep={currentStep}>{renderStep()}</ActivationLayout>;
};

export default Activation;
